import * as React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { useController } from "react-hook-form";
import { makeStyles, styled, VeBox, VeTypography } from "../../mui";

interface Options {
  inputValue?: string;
  label: string;
  value?: number | string;
}

const useStyles = makeStyles({
  custom: {
    "& .MuiFormControl-root  .MuiAutocomplete-inputRoot": {
      padding: "2px 9px !important",
      paddingRight: "26px !important",
    },
    "& .MuiAutocomplete-popupIndicator": {
      visibility: "visible",
    },
  },
});

const filter = createFilterOptions<Options>();

export function CreatableSelect({
  label,
  control,
  options,
  name,
  value,
  setValue,
  style,
  error,
  errorText,
  disabled,
  required,
  isAdded = true,
  onChange,
  freeSolo = true,
  noMargin = false,
  colorCube = false,
}: {
  label: string;
  control?: any;
  options: Options[];
  name: string;
  value?: any;
  style?: React.CSSProperties;
  setValue?: any;
  error?: boolean;
  errorText?: string;
  disabled?: boolean;
  required?: boolean;
  isAdded?: boolean;
  onChange?: (value: any) => void;
  freeSolo?: boolean;
  noMargin?: boolean;
  colorCube?: boolean; // Renders a small square with bg color of its value. Mostly used for color picking using VeSelect
}) {
  const classes = useStyles();

  const DropDownIcon = ({
    disabled,
    ...props
  }: React.SVGProps<SVGSVGElement> & { disabled?: boolean }) => {
    return (
      <svg
        style={{ marginTop: "-1px" }}
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
      >
        <path
          d="M9.411 13.0896C9.73642 13.4151 10.2649 13.4151 10.5903 13.0896L15.5886 8.09132C15.914 7.76591 15.914 7.23744 15.5886 6.91203C15.2632 6.58662 14.7347 6.58662 14.4093 6.91203L9.99935 11.322L5.58937 6.91463C5.26396 6.58922 4.73549 6.58922 4.41007 6.91463C4.08466 7.24004 4.08466 7.76851 4.41007 8.09392L9.4084 13.0923L9.411 13.0896Z"
          fill={disabled ? "#8F9295" : "#3D465A"}
        />
      </svg>
    );
  };

  const {
    field: { value: controlValue, onChange: controlOnChange },
  } = control
    ? useController({
        name: name || "",
        control,
      })
    : { field: { value: null, onChange: () => {} } };

  // --- Helper: resolve value safely
  const findOptionByValue = (val: any) => {
    if (val == null) return null;

    if (typeof val === "object") return val;

    return (
      options.find((opt) => opt.value === val) ?? {
        label: val,
        value: val,
      }
    );
  };

  console.log(value);

  // --- Determine which value to show
  const currentValue = value !== undefined ? value : controlValue;
  const selectedOption =
    currentValue !== null && currentValue !== undefined
      ? findOptionByValue(currentValue)
      : null;

  const StyledTextField = styled(TextField)(() => ({
    "& .MuiFormHelperText-root .Mui-error": {
      color: "#DE1135",
      fontFamily: "var(--font-inter)",
      fontSize: "10px",
      lineHeight: "14px",
    },
    "& .MuiFormHelperText-root": {
      color: "#646E83",
      fontFamily: "var(--font-inter)",
      fontSize: "10px",
      lineHeight: "14px",
    },
    "& .MuiInputLabel-root.MuiInputLabel-asterisk": {
      color: "#DE1135",
    },
  }));

  return (
    <VeBox width={"100%"} display={"flex"} flexDirection={"column"}>
      <Autocomplete
        popupIcon={<DropDownIcon disabled={disabled} />}
        value={selectedOption}
        sx={{
          ...style,
          width: "100%",
          "& .MuiOutlinedInput-root": {
            borderRadius: "4px !important",
          },
        }}
        disabled={disabled}
        className={classes.custom}
        onChange={(event, newValue) => {
          let finalValue = "";
          if (typeof newValue === "string") {
            finalValue = newValue;
          } else if (newValue?.inputValue) {
            finalValue = newValue.inputValue;
          } else if (newValue?.value) {
            finalValue = newValue.value;
          }

          if (control) controlOnChange(finalValue);
          if (setValue) setValue(finalValue);
          if (onChange) onChange(newValue);
        }}
        filterOptions={
          isAdded
            ? (options, params) => {
                const filtered = filter(options, params);
                const { inputValue } = params;
                const isExisting = options.some(
                  (option) => inputValue === option.label,
                );
                if (inputValue !== "" && !isExisting) {
                  filtered.push({
                    inputValue,
                    label: `Add "${inputValue}"`,
                    value: inputValue,
                  });
                }
                return filtered;
              }
            : undefined
        }
        selectOnFocus
        autoHighlight
        clearOnBlur
        handleHomeEndKeys
        id="free-solo-with-text-demo"
        options={options}
        getOptionLabel={(option) => {
          if (typeof option === "string") return option;
          if (option.inputValue) return option.inputValue;
          return option.label;
        }}
        noOptionsText={
          <VeTypography variant="label3Secondary">No Options</VeTypography>
        }
        renderOption={(props: any, option) => {
          const { key, ...optionProps } = props;
          return (
            <li key={option.value ?? option.label} {...optionProps}>
              <VeBox display={"flex"} alignItems={"center"} gap={"12px"}>
                {/* THIS IS USED TO DISPLAY A COLOR NEXT TO AN OPTION , mostly used for picking colours */}
                {colorCube && (
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      backgroundColor: (option?.value as string) || "white",
                    }}
                  />
                )}
                <VeTypography variant="label2Secondary">
                  {option.label}
                </VeTypography>
              </VeBox>
            </li>
          );
        }}
        freeSolo={freeSolo}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "#3D465A !important",
              },
              "& .MuiOutlinedInput-root.Mui-focused": {
                color: "#1D2330 !important",
              },
              "& .MuiOutlinedInput-input": {
                color: "inherit",
              },
            }}
            error={error}
            label={
              required ? (
                <VeBox
                  sx={{
                    color: disabled
                      ? "#8F9295 !important"
                      : "#3D465A !important",
                  }}
                  display={"flex"}
                >
                  {label}
                  <VeBox marginLeft={"2px"} color={"red"}>
                    *
                  </VeBox>
                </VeBox>
              ) : (
                <VeBox
                  sx={{
                    color: disabled
                      ? "#8F9295 !important"
                      : "#3D465A !important",
                  }}
                >
                  {label}
                </VeBox>
              )
            }
          />
        )}
      />
      {error && errorText && (
        <VeBox
          sx={{ marginTop: noMargin ? "0px !important" : "-6px !important" }}
        >
          <VeTypography
            style={{ marginLeft: "10px", marginTop: "0px !important" }}
            variant="label4ErrorPrimary"
          >
            {errorText}
          </VeTypography>
        </VeBox>
      )}
    </VeBox>
  );
}
